/* eslint-disable react/function-component-definition */
import * as Sentry from '@sentry/react';
import AppCallback from 'components/appCallback/AppCallback';
import AppPayment from 'components/appPayment/AppPayment';
import AppStatus from 'components/appStatus/AppStatus';
import CommonLayout from 'components/commonLayout/CommonLayout';
import ErrorViewSelector from 'components/errorViewSelector/ErrorViewSelector';
import { ERROR_SCREEN_TYPES } from 'config/config';
import 'isomorphic-fetch';
import React, { ErrorInfo, PureComponent } from 'react';
import { BrowserRouter as Router, Route, RouteComponentProps, Switch } from 'react-router-dom';
import LocaleProvider from 'services/LocaleProvider/LocaleProvider';
import { PaymentPartial } from 'types/Payment';
import AppPaymentInitFail from '../appPaymentInitFailed/AppPaymentInitFail';
import RedirectScreen from '../redirect/screen/RedirectScreen';
import { ClientMetricsProvider } from '../../services/ClientMetricsContext';
import { CardFormMetricsProvider } from '../../services/CardFormMetricsContext';
import { AggregatedMetricsProvider } from '../../services/AggregatedMetricsContext';

const BASE_URL = process.env.PUBLIC_URL || '/';

type RouteType = RouteComponentProps<{ paymentId: string }>;

const routesWithProps = (setPaymentDetails: (paymentDetails: PaymentPartial, paymentId: string) => void) => {
  const Payment = (props: RouteType) => <AppPayment {...props} setPaymentDetails={setPaymentDetails} />;
  const Callback = (props: RouteType) => <AppCallback {...props} setPaymentDetails={setPaymentDetails} />;
  const InitFailed = (props: RouteType) => <AppPaymentInitFail {...props} setPaymentDetails={setPaymentDetails} />;
  const Status = (props: RouteType) => <AppStatus {...props} setPaymentDetails={setPaymentDetails} />;
  const Unavailable = (props: RouteType) => <ErrorViewSelector {...props} screenType={ERROR_SCREEN_TYPES.DEFAULT} />;
  const Err = (props: RouteType) => <ErrorViewSelector {...props} screenType={ERROR_SCREEN_TYPES.PAYMENT} />;

  return (
    <Switch>
      <Route path="/unavailable" exact render={Unavailable} />
      <Route path="/error" exact render={Err} />
      <Route path="/:paymentId" exact render={Payment} />
      <Route path="/:paymentId/callback" exact render={Callback} />
      <Route path="/:paymentId/status" exact render={Status} />
      <Route path="/:paymentId/init-failed" exact render={InitFailed} />
      <Route component={ErrorViewSelector} />
    </Switch>
  );
};

interface IErrorState {
  error?: Error;
}

class App extends PureComponent<object, IErrorState> {
  constructor(props) {
    super(props);
    this.state = { error: undefined };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error });
    Sentry.withScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key]);
      });
      Sentry.captureException(error);
    });
  }

  render() {
    const { error } = this.state;
    return (
      <LocaleProvider>
        <CardFormMetricsProvider>
          <ClientMetricsProvider>
            <AggregatedMetricsProvider>
              <Sentry.ErrorBoundary
                fallback={errorData => (
                  <CommonLayout render={() => <div />} error={errorData.error}>
                    <ErrorViewSelector />
                  </CommonLayout>
                )}
              >
                <Router>
                  <Switch>
                    {!error && (
                      <Route path={`${BASE_URL}:paymentId/notification/:type`}>
                        <RedirectScreen />
                      </Route>
                    )}
                    <Route path={BASE_URL}>
                      <CommonLayout render={routesWithProps} error={error} />
                    </Route>
                  </Switch>
                </Router>
              </Sentry.ErrorBoundary>
            </AggregatedMetricsProvider>
          </ClientMetricsProvider>
        </CardFormMetricsProvider>
      </LocaleProvider>
    );
  }
}

export default App;
